import {
  addPlaceholderToCache,
  aiTagIdFromName,
  groupAiTagsByCategory,
  isValidNewAiTagName,
  remapAiTagIds,
  useAiTags,
  useSaveAiTags,
} from '@solinkcorp/ai-tags';
import { useUserSession } from '@solinkcorp/authentication';
import { useLocale } from '@solinkcorp/locale';
import {
  Button,
  cn,
  CreatableMultiSelect,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  Input,
  SelectItem,
  SimpleFormField,
  SimpleSelect,
  type CreatableMultiSelectOption as Option,
} from '@solinkcorp/solink-ui';
import { assertNever } from '@solinkcorp/utils';
import { type Immutable } from 'immer';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useControllerState } from 'solox';
import {
  DEFAULT_COUNT_THRESHOLD,
  DEFAULT_LIVE_FEED_ROTATION_SECONDS,
  DEFAULT_TIME_THRESHOLD_SECONDS,
  FIXED_DRIVE_OFF_THRESHOLD,
  MAX_LIVE_FEED_WIDGETS_PER_LAYOUT,
  SDM_EVENTS,
} from '../../constants';
import { useSDMAnalytics } from '../../contexts/AnalyticsContext';
import { type Controller } from '../../Controller';
import { useServiceDisplayDayParts } from '../../hooks/useServiceDisplayDayParts';
import { type DayPartThreshold, type ServiceDisplayWidget } from '../../layout/types';
import { sdmDialogContentStyles } from '../Monitor/LayoutSelector/LayoutSelectorStyles';
import { getWidgetAnalytics } from '../widgets/widgetAnalytics';
import { CountWidgetSettings } from './CountWidgetSettings';
import { JourneyWidgetSettings } from './JourneyWidgetSettings';
import { LiveFeedWidgetSettings } from './LiveFeedWidgetSettings';
import { i18nZodResolver, WIDGET_FORM_SCHEMA, type WidgetFormData } from './schema';
import { ThresholdComponent } from './ThresholdComponent';
import { TimeWidgetSettings } from './TimeWidgetSettings';

interface WidgetDialogProps {
  controller: Controller;
}

interface WidgetDialogInnerProps {
  controller: Controller;
  widget: Immutable<ServiceDisplayWidget>;
  /** Day-part thresholds from controller (draft or widget); single source of truth. */
  thresholds: DayPartThreshold[];
  onThresholdsChange: (thresholds: DayPartThreshold[]) => void;
  /** Day part IDs enabled in other widgets in this layout (for layout-wide overlap). From parent so it's correct on first render. */
  otherWidgetDayPartIds: readonly string[];
  /** Live feed widgets already in this layout, excluding the one being configured. */
  otherLiveFeedWidgetCount: number;
}

/** Displays a dialog for configuring an existing or new widget. */
const WidgetConfigurationDialog: React.FC<WidgetDialogProps> = ({ controller }) => {
  const { widget, otherWidgetDayPartIds, otherLiveFeedWidgetCount, configuringWidgetThresholds } =
    useControllerState(controller.state, (state) => {
      const {
        configuringWidgetId: cid,
        editingLayout,
        pendingNewWidget,
        configuringWidgetThresholds,
      } = state;
      if (!cid || !editingLayout) {
        return {
          widget: undefined as Immutable<ServiceDisplayWidget> | undefined,
          otherWidgetDayPartIds: [] as string[],
          otherLiveFeedWidgetCount: 0,
          configuringWidgetThresholds: undefined as DayPartThreshold[] | undefined,
        };
      }
      const widgets = editingLayout.widgets;
      const widgetFound =
        pendingNewWidget?.id === cid ? pendingNewWidget : widgets.find((w) => w.id === cid);
      const ids = new Set<string>();
      let liveFeedCount = 0;
      for (const w of widgets) {
        if (w.id === cid) continue;
        if (w.config.type === 'LIVE_FEED') liveFeedCount += 1;
        for (const t of w.thresholds ?? []) {
          if (t.dayPartsId) ids.add(t.dayPartsId);
        }
      }
      return {
        widget: widgetFound,
        otherWidgetDayPartIds: Array.from(ids),
        otherLiveFeedWidgetCount: liveFeedCount,
        configuringWidgetThresholds,
      };
    });

  const thresholds: DayPartThreshold[] = configuringWidgetThresholds
    ? [...configuringWidgetThresholds]
    : (widget?.thresholds?.map((t) => ({ ...t })) ?? []);

  return widget ? (
    <WidgetConfigurationForm
      controller={controller}
      widget={widget}
      thresholds={thresholds}
      onThresholdsChange={(t) => controller.setConfiguringWidgetThresholds(t)}
      otherWidgetDayPartIds={otherWidgetDayPartIds}
      otherLiveFeedWidgetCount={otherLiveFeedWidgetCount}
      key={widget.id}
    />
  ) : null;
};

const WidgetConfigurationForm: React.FC<WidgetDialogInnerProps> = ({
  controller,
  widget,
  thresholds: dayPartThresholds,
  onThresholdsChange,
  otherWidgetDayPartIds,
  otherLiveFeedWidgetCount,
}) => {
  const locale = useLocale();
  const analytics = useSDMAnalytics();
  const user = useUserSession();
  const { data: aiTags, isLoading: aiTagsLoading } = useAiTags();
  const saveAiTags = useSaveAiTags();
  const { data: dayParts = [] } = useServiceDisplayDayParts();

  const idSet = React.useMemo(() => new Set(otherWidgetDayPartIds), [otherWidgetDayPartIds]);
  const dayPartsEnabledInOtherWidgetsInLayout = dayParts.filter((dp: { id: string }) =>
    idSet.has(dp.id),
  );

  // Grouped picker options: server-categorized tags rendered with separators.
  // Newly-typed tags are seeded into the SWR cache by `onCreateOption` and flow
  // through `useAiTags` → `groupAiTagsByCategory` into the Custom group.
  const aiTagOptionGroups = useMemo(
    () =>
      groupAiTagsByCategory(aiTags).map((g) => ({
        label: g.label,
        options: [...g.options],
      })),
    [aiTags],
  );

  // Flat option lookup for label/value queries (analytics, idRemap, selected chip rendering).
  const aiTagOptions: Option[] = useMemo(
    () => aiTagOptionGroups.flatMap((g) => g.options),
    [aiTagOptionGroups],
  );

  const onOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        controller.discardWidgetEdits();
      }
    },
    [controller],
  );

  // Work out default values for the form from the existing widget configuration.
  const defaultValues = useMemo(() => {
    try {
      const formData = convertWidgetToFormData(widget);
      return formData;
    } catch (err) {
      console.error('Failed to convert widget to form data', err);
      const timeThreshold = totalSecondsToMinutesAndSeconds(DEFAULT_TIME_THRESHOLD_SECONDS);
      return {
        title: locale.serviceDisplayMonitor.newWidget,
        tagIds: [],
        type: 'COUNT' as const,
        countThreshold: DEFAULT_COUNT_THRESHOLD,
        horizon: 'realtime' as const,
        timeThresholdMinutes: timeThreshold.minutes,
        timeThresholdSeconds: timeThreshold.seconds,
      };
    }
  }, [widget, locale.serviceDisplayMonitor.newWidget]);

  const form = useForm<WidgetFormData>({
    resolver: i18nZodResolver(),
    defaultValues,
  });

  const onCreateTag = React.useCallback(
    (inputValue: string): Option => {
      const id = aiTagIdFromName(inputValue, user.customerId);
      // Seed the placeholder so useAiTags resolves id → name immediately and
      // useSaveAiTags POSTs the correct name on submit.
      void addPlaceholderToCache([{ id, name: inputValue }], user);
      const currentTagIds = form.getValues('tagIds');
      form.setValue('tagIds', [...currentTagIds, id], { shouldDirty: true });
      return { value: id, label: inputValue };
    },
    [form, user],
  );

  const onSubmit = useCallback(
    (values: WidgetFormData) => {
      const assignedTagLabel = aiTagOptions.find((tag) => tag.value === values.tagIds[0])?.label;

      analytics.trackSDMEvent(SDM_EVENTS.SAVE_WIDGET, {
        description: 'Saved widget',
        ...getWidgetAnalytics(values, assignedTagLabel),
      });

      // Update layout with form values and thresholds immediately so thresholds are never lost
      const updatedValues: WidgetFormData = { ...values, tagIds: values.tagIds };
      const updatedWidget = updateWidgetWithFormValues(widget, updatedValues, dayPartThresholds);
      controller.updateWidget(updatedWidget);

      saveAiTags(values.tagIds)
        .then((idRemap) => {
          const finalTagIds = remapAiTagIds(values.tagIds, idRemap);
          form.setValue('tagIds', finalTagIds, { shouldDirty: true });
          const withResolvedTags = updateWidgetWithFormValues(
            widget,
            { ...values, tagIds: finalTagIds },
            dayPartThresholds,
          );
          controller.updateWidget(withResolvedTags);
        })
        .catch((error) => {
          console.error('Error saving service display AI tags', error);
        });
    },
    [aiTagOptions, saveAiTags, form, widget, controller, dayPartThresholds, analytics],
  );

  const widgetType = form.watch('type');
  const liveFeedLimitReached = otherLiveFeedWidgetCount >= MAX_LIVE_FEED_WIDGETS_PER_LAYOUT;

  useEffect(() => {
    // Switching onto LIVE_FEED from a metric type brings no rotation interval with it, so seed
    // one before the input renders or it would start out uncontrolled.
    if (widgetType === 'LIVE_FEED' && form.getValues('rotationSeconds') === undefined) {
      form.setValue('rotationSeconds', DEFAULT_LIVE_FEED_ROTATION_SECONDS);
    }
    // LIVE_FEED stores no horizon or threshold, so switching away from it has to seed both
    // before the metric settings render, or their inputs would start out uncontrolled.
    if (widgetType !== 'LIVE_FEED' && form.getValues('horizon') === undefined) {
      form.setValue('horizon', widgetType === 'DRIVE_OFF' ? '1h' : 'realtime');
      if (form.getValues('countThreshold') === undefined) {
        form.setValue('countThreshold', DEFAULT_COUNT_THRESHOLD);
      }
    }

    if (widgetType === 'TOTAL_JOURNEY_TIME') {
      // Journey widgets have no realtime horizon.
      if (form.getValues('horizon') === 'realtime') {
        form.setValue('horizon', '1h');
      }

      const currentTitle = form.getValues('title');
      const isUntouchedTitle =
        !form.getFieldState('title').isDirty &&
        (!currentTitle ||
          currentTitle === 'New Widget' || // Check hardcoded default from Controller.ts in case translations are not loaded yet
          currentTitle === locale.serviceDisplayMonitor.newWidget);
      if (isUntouchedTitle) {
        form.setValue('title', locale.serviceDisplayMonitor.totalJourneyTime);
      }
      if (
        form.getValues('timeThresholdMinutes') === undefined &&
        form.getValues('timeThresholdSeconds') === undefined
      ) {
        const timeThreshold = totalSecondsToMinutesAndSeconds(DEFAULT_TIME_THRESHOLD_SECONDS);
        form.setValue('timeThresholdMinutes', timeThreshold.minutes);
        form.setValue('timeThresholdSeconds', timeThreshold.seconds);
      }
    }

    if (widgetType === 'DRIVE_OFF' && form.getValues('horizon') === 'realtime') {
      // DRIVE_OFF doesn't support realtime; default to 1h if the user switches type while
      // realtime was selected on the previous type.
      form.setValue('horizon', '1h');
    }
    // When editing a COUNT/DRIVE_OFF widget, the form data is not initialized with a time threshold
    // do it now with default value if we detect that's the case on widget type switch
    if (
      widgetType === 'TIME' &&
      form.getValues('timeThresholdMinutes') === undefined &&
      form.getValues('timeThresholdSeconds') === undefined
    ) {
      const timeThreshold = totalSecondsToMinutesAndSeconds(DEFAULT_TIME_THRESHOLD_SECONDS);
      form.setValue('timeThresholdMinutes', timeThreshold.minutes);
      form.setValue('timeThresholdSeconds', timeThreshold.seconds);
    }
  }, [
    widgetType,
    form,
    locale.serviceDisplayMonitor.newWidget,
    locale.serviceDisplayMonitor.totalJourneyTime,
  ]);

  return (
    <Dialog open={true} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(sdmDialogContentStyles, 'flex max-h-[90dvh] flex-col')}
        onPointerDownOutside={keepDialogOpenForTagMenu}
        onFocusOutside={keepDialogOpenForTagMenu}
        onInteractOutside={keepDialogOpenForTagMenu}
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex min-h-0 flex-1 flex-col gap-0"
          >
            <DialogHeader>
              <DialogTitle>{locale.serviceDisplayMonitor.editWidget}</DialogTitle>
            </DialogHeader>

            <div className="min-h-0 flex-1 space-y-8 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <SimpleFormField
                control={form.control}
                name="type"
                label={locale.dashboard.type}
                description={locale.serviceDisplayMonitor.chooseToDisplayWidgetType}
                render={({ field }) => (
                  <SimpleSelect
                    className="w-[100%]"
                    data-testid="sdm-widget-type-select"
                    {...field}
                  >
                    <SelectItem value="COUNT" data-testid="sdm-widget-type-option-COUNT">
                      {locale.serviceDisplayMonitor.count}
                    </SelectItem>
                    <SelectItem value="TIME" data-testid="sdm-widget-type-option-TIME">
                      {locale.serviceDisplayMonitor.time}
                    </SelectItem>
                    <SelectItem value="DRIVE_OFF" data-testid="sdm-widget-type-option-DRIVE_OFF">
                      {locale.serviceDisplayMonitor.driveOff}
                    </SelectItem>
                    <SelectItem
                      value="LIVE_FEED"
                      disabled={liveFeedLimitReached}
                      data-testid="sdm-widget-type-option-LIVE_FEED"
                    >
                      {liveFeedLimitReached
                        ? `${locale.serviceDisplayMonitor.liveFeed} (${locale.serviceDisplayMonitor.maxLiveFeedWidgetsReached})`
                        : locale.serviceDisplayMonitor.liveFeed}
                    </SelectItem>
                    <SelectItem
                      value="TOTAL_JOURNEY_TIME"
                      data-testid="sdm-widget-type-option-TOTAL_JOURNEY_TIME"
                    >
                      {locale.serviceDisplayMonitor.totalJourneyTime}
                    </SelectItem>
                  </SimpleSelect>
                )}
              />

              <SimpleFormField
                control={form.control}
                name="title"
                label={locale.general.name}
                render={({ field }) => (
                  <Input
                    {...field}
                    data-testid="sdm-widget-name-input"
                    className="focus-visible:border-teal-500 focus-visible:ring-[3px] focus-visible:ring-teal-500/50"
                  />
                )}
              />

              <SimpleFormField
                control={form.control}
                name="tagIds"
                label={locale.serviceDisplayMonitor.aiTags}
                description={locale.serviceDisplayMonitor.assignTagsToDisplayData}
                render={({ field }) => {
                  const selectedOptions = (field.value || [])
                    .map((tagId: string) => {
                      const existingTag = aiTagOptions.find((option) => option.value === tagId);
                      if (existingTag) {
                        return {
                          label: existingTag.label,
                          value: tagId,
                        } as Option;
                      }
                      return null;
                    })
                    .filter((option): option is Option => option !== null);

                  return (
                    <CreatableMultiSelect
                      data-testid="sdm-widget-ai-tags"
                      options={aiTagOptionGroups}
                      value={selectedOptions}
                      onChange={(options) => {
                        const tagIds = options.map((option) => option.value);
                        field.onChange(tagIds);
                      }}
                      onCreateOption={(inputValue) => onCreateTag(inputValue)}
                      isValidNewOption={(inputValue) =>
                        isValidNewAiTagName(inputValue, aiTagOptionGroups)
                      }
                      placeholder={
                        aiTagsLoading
                          ? locale.serviceDisplayMonitor.loadingTags
                          : locale.serviceDisplayMonitor.selectOrCreateTags
                      }
                      isDisabled={aiTagsLoading}
                      menuInPortal
                    />
                  );
                }}
              />

              {widgetType === 'TIME' && (
                <TimeWidgetSettings
                  form={form}
                  hasDayPartThresholds={dayPartThresholds.length > 0}
                  hasAnyDayParts={dayParts.length > 0}
                />
              )}
              {widgetType === 'TOTAL_JOURNEY_TIME' && (
                <JourneyWidgetSettings
                  form={form}
                  hasDayPartThresholds={dayPartThresholds.length > 0}
                  hasAnyDayParts={dayParts.length > 0}
                />
              )}
              {(widgetType === 'COUNT' || widgetType === 'DRIVE_OFF') && (
                <CountWidgetSettings
                  form={form}
                  hasDayPartThresholds={dayPartThresholds.length > 0}
                  hasAnyDayParts={dayParts.length > 0}
                />
              )}
              {widgetType === 'LIVE_FEED' && <LiveFeedWidgetSettings form={form} />}

              {/* Live feeds have no metric to colour, so they take no day part thresholds.
                  Drive-off widgets surface the day-part assignment section only for the
                  `currentDayPart` horizon. The other horizons don't combine with a day-part
                  filter (realtime/currentHour/currentDay) */}
              {widgetType !== 'LIVE_FEED' &&
                (widgetType !== 'DRIVE_OFF' || form.watch('horizon') === 'currentDayPart') && (
                  <ThresholdComponent
                    // Journey thresholds are durations, so they reuse the TIME row editor.
                    widgetType={
                      (widgetType === 'TOTAL_JOURNEY_TIME' ? 'TIME' : widgetType) as
                        'COUNT' | 'TIME' | 'DRIVE_OFF'
                    }
                    widget={widget as ServiceDisplayWidget}
                    allDayParts={dayParts}
                    thresholds={dayPartThresholds}
                    onThresholdsChange={onThresholdsChange}
                    dayPartsEnabledInOtherWidgetsInLayout={dayPartsEnabledInOtherWidgetsInLayout}
                  />
                )}
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button
                  className="cursor-pointer text-muted-foreground dark:text-white"
                  variant="outline"
                  onClick={controller.discardWidgetEdits}
                >
                  {locale.general.cancel}
                </Button>
              </DialogClose>
              <Button
                className="hover:bg-ui-modal-darker-inner-section cursor-pointer border-none bg-turquoise-500 text-white hover:text-white"
                variant="default"
                type="submit"
                data-testid="sdm-widget-save-button"
              >
                {locale.general.save}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(WidgetConfigurationDialog);

/** The tag menu is portaled to `document.body`, outside the dialog node. */
function keepDialogOpenForTagMenu(event: {
  preventDefault: () => void;
  detail: { originalEvent: Event };
}) {
  const target = event.detail.originalEvent.target;
  if (target instanceof Element && target.closest('[data-slot="creatable-multi-select-menu"]')) {
    event.preventDefault();
  }
}

/**
 * Convert minutes and seconds to total seconds.
 */
function minutesAndSecondsToTotalSeconds(minutes: number = 0, seconds: number = 0): number {
  return minutes * 60 + seconds;
}

/**
 * Convert total seconds to minutes and seconds.
 */
function totalSecondsToMinutesAndSeconds(totalSeconds: number): {
  minutes: number;
  seconds: number;
} {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return { minutes, seconds };
}

/** Returns an updated copy of the `widget` with values from the form data. */
function updateWidgetWithFormValues(
  widget: Immutable<ServiceDisplayWidget>,
  values: WidgetFormData,
  thresholds: DayPartThreshold[],
): ServiceDisplayWidget {
  let updatedWidget: ServiceDisplayWidget;
  switch (values.type) {
    case 'COUNT':
      updatedWidget = {
        ...widget,
        title: values.title,
        tagIds: values.tagIds,
        config: {
          type: 'COUNT',
          threshold: values.countThreshold,
          horizon: values.horizon,
        },
        thresholds,
      };
      break;
    case 'DRIVE_OFF':
      updatedWidget = {
        ...widget,
        title: values.title,
        tagIds: values.tagIds,
        config: {
          type: 'DRIVE_OFF',
          threshold: FIXED_DRIVE_OFF_THRESHOLD,
          horizon: values.horizon,
        },
        thresholds,
      };
      break;
    case 'TIME': {
      const thresholdSeconds = minutesAndSecondsToTotalSeconds(
        values.timeThresholdMinutes,
        values.timeThresholdSeconds,
      );

      updatedWidget = {
        ...widget,
        title: values.title,
        tagIds: values.tagIds,
        config: {
          type: 'TIME',
          threshold: thresholdSeconds,
          horizon: values.horizon,
        },
        thresholds,
      };
      break;
    }
    case 'TOTAL_JOURNEY_TIME': {
      const thresholdSeconds = minutesAndSecondsToTotalSeconds(
        values.timeThresholdMinutes,
        values.timeThresholdSeconds,
      );

      updatedWidget = {
        ...widget,
        title: values.title,
        tagIds: values.tagIds,
        config: {
          type: 'TOTAL_JOURNEY_TIME',
          threshold: thresholdSeconds,
          horizon: values.horizon,
        },
        thresholds,
      };
      break;
    }
    case 'LIVE_FEED':
      updatedWidget = {
        ...widget,
        title: values.title,
        tagIds: values.tagIds,
        config: { type: 'LIVE_FEED', rotationSeconds: values.rotationSeconds },
        // Day part thresholds only shape metric colouring, which a live feed doesn't have.
        thresholds: [],
      };

      break;
    default:
      assertNever(values);
  }
  return updatedWidget;
}

/**
 * Extract form data out of a widget.
 */
function convertWidgetToFormData(widget: Immutable<ServiceDisplayWidget>): WidgetFormData {
  const { config } = widget;

  if (config.type === 'LIVE_FEED') {
    return WIDGET_FORM_SCHEMA.parse({
      title: widget.title,
      tagIds: widget.tagIds.slice(),
      type: 'LIVE_FEED' as const,
      // Widgets saved before this setting existed have no interval; the schema default applies.
      rotationSeconds: config.rotationSeconds ?? DEFAULT_LIVE_FEED_ROTATION_SECONDS,
    });
  }

  if (config.type === 'TIME') {
    const thresholdTime = totalSecondsToMinutesAndSeconds(config.threshold);
    return WIDGET_FORM_SCHEMA.parse({
      title: widget.title,
      tagIds: widget.tagIds.slice(),
      type: 'TIME' as const,
      timeThresholdMinutes: thresholdTime.minutes,
      timeThresholdSeconds: thresholdTime.seconds,
      horizon: config.horizon ?? 'realtime',
    });
  }

  if (config.type === 'TOTAL_JOURNEY_TIME') {
    const thresholdTime = totalSecondsToMinutesAndSeconds(config.threshold);
    return WIDGET_FORM_SCHEMA.parse({
      title: widget.title,
      tagIds: widget.tagIds.slice(),
      type: 'TOTAL_JOURNEY_TIME' as const,
      timeThresholdMinutes: thresholdTime.minutes,
      timeThresholdSeconds: thresholdTime.seconds,
      // Journey widgets have no realtime horizon, so fall back to the 1h window.
      horizon: config.horizon ?? '1h',
    });
  }

  if (config.type === 'DRIVE_OFF') {
    return WIDGET_FORM_SCHEMA.parse({
      title: widget.title,
      tagIds: widget.tagIds.slice(),
      type: 'DRIVE_OFF' as const,
      // The threshold is hidden in the UI for DRIVE_OFF and always normalized to the fixed
      // value on save; heal any historical drift (0 / undefined) on read too.
      countThreshold: config.threshold || FIXED_DRIVE_OFF_THRESHOLD,
      horizon: config.horizon ?? '1h',
    });
  }

  return WIDGET_FORM_SCHEMA.parse({
    title: widget.title,
    tagIds: widget.tagIds.slice(),
    type: 'COUNT' as const,
    countThreshold: config.threshold ?? DEFAULT_COUNT_THRESHOLD,
    horizon: config.horizon ?? 'realtime',
  });
}
